import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ReportConfig, SearchFilters } from '@/types';

// ── Export report as PDF ────────────────────────────────

interface ExportReportParams {
  config: ReportConfig;
  filters?: SearchFilters;
}

async function exportReportPdf(params: ExportReportParams): Promise<string> {
  const response = await api.post('/reporting/pdf/report', params, {
    responseType: 'text',
  });
  return response.data;
}

export function useExportReportPdf() {
  return useMutation({
    mutationFn: exportReportPdf,
    onSuccess: html => {
      openPdfPreview(html);
    },
  });
}

// ── Export credit pack as PDF ───────────────────────────

async function exportCreditPackPdf(loanId: string): Promise<string> {
  const response = await api.get(`/reporting/pdf/credit-pack/${loanId}`, {
    responseType: 'text',
  });
  return response.data;
}

export function useExportCreditPackPdf() {
  return useMutation({
    mutationFn: exportCreditPackPdf,
    onSuccess: html => {
      openPdfPreview(html);
    },
  });
}

// ── Export invoice as PDF ───────────────────────────────

async function exportInvoicePdf(invoiceId: string): Promise<string> {
  const response = await api.get(`/reporting/pdf/invoice/${invoiceId}`, {
    responseType: 'text',
  });
  return response.data;
}

export function useExportInvoicePdf() {
  return useMutation({
    mutationFn: exportInvoicePdf,
    onSuccess: html => {
      openPdfPreview(html);
    },
  });
}

// ── Export settlement as PDF ────────────────────────────

async function exportSettlementPdf(settlementId: string): Promise<string> {
  const response = await api.get(`/reporting/pdf/settlement/${settlementId}`, {
    responseType: 'text',
  });
  return response.data;
}

export function useExportSettlementPdf() {
  return useMutation({
    mutationFn: exportSettlementPdf,
    onSuccess: html => {
      openPdfPreview(html);
    },
  });
}

// ── Export compliance report as PDF ─────────────────────

async function exportCompliancePdf(filters?: SearchFilters): Promise<string> {
  const response = await api.post('/reporting/pdf/compliance', filters || {}, {
    responseType: 'text',
  });
  return response.data;
}

export function useExportCompliancePdf() {
  return useMutation({
    mutationFn: exportCompliancePdf,
    onSuccess: html => {
      openPdfPreview(html);
    },
  });
}

// ── Utility: Open PDF preview in new window ─────────────

function openPdfPreview(html: string) {
  printHtmlPreview(html);
}

export function printHtmlPreview(html: string) {
  const printWindow = window.open('', '', 'width=1200,height=800');
  if (!printWindow) {
    return;
  }

  let cleanedUp = false;
  let fallbackTimer: number | undefined;

  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;

    if (fallbackTimer !== undefined) {
      window.clearTimeout(fallbackTimer);
    }

    printWindow.removeEventListener('afterprint', handleAfterPrint);

    try {
      printWindow.close();
    } catch {
      // Ignore popup close failures.
    }

    try {
      window.focus();
    } catch {
      // Ignore focus failures.
    }
  };

  const handleAfterPrint = () => {
    cleanup();
  };

  const triggerPrint = () => {
    if (cleanedUp) return;

    try {
      printWindow.focus();
      printWindow.print();
    } catch {
      cleanup();
    }
  };

  printWindow.addEventListener('afterprint', handleAfterPrint);
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  window.requestAnimationFrame(triggerPrint);
  fallbackTimer = window.setTimeout(cleanup, 15000);
}

export { openPdfPreview };
